import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAppSelector } from "../../store/hooks";
import {
  Calendar,
  Clock,
  MapPin,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Mail,
  UserCheck,
  Sun,
  Moon,
  Link as LinkIcon,
  Globe,
  Shirt,
  ArrowRight,
} from "lucide-react";
import CountdownTimer from "../../components/CountdownTimer";
import { getImageShapeStyles } from "../../utils/imageShapeStyles";
import { logError } from "../../utils/errorLogger";
import { apiClient } from "../../services/apiClient";
import { roleSwitchingService } from "../../services/roleSwitchingService";

// API Configuration - matches pattern used in other service files
const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";
const MEDIA_BASE_URL = process.env.REACT_APP_MEDIA_URL || "/media";

// Background theme definitions
const defaultThemes = {
  stars: {
    name: "Starry Night",
    description: "Animated starry background",
    background: "url(/assets/backgrounds/gifs/background_stars.gif)",
    preview: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
    fallback: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
    type: "gif",
  },
  background_ghost: {
    name: "Ghost Background",
    description: "Animated ghost background",
    background: `url(${MEDIA_BASE_URL}/backgrounds/gifs/background_ghost.gif)`,
    preview: `url(${MEDIA_BASE_URL}/backgrounds/gifs/background_ghost.gif)`,
    fallback: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
    type: "gif",
  },
  background_waves: {
    name: "Waves Background",
    description: "Animated waves background",
    background: `url(${MEDIA_BASE_URL}/backgrounds/gifs/background_waves.gif)`,
    preview: `url(${MEDIA_BASE_URL}/backgrounds/gifs/background_waves.gif)`,
    fallback: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
    type: "gif",
  },
};

interface CompanionInvitationData {
  guest_event_id: string;
  invitation_token: string;
  guest_id: string;
  guest_name: string;
  nickname?: string;
  display_name: string;
  guest_phone?: string;
  status: 'pending' | 'accepted' | 'rejected' | 'ignored';
  relationship?: string;
}

interface EventData {
  event_id: string;
  event_title: string;
  event_date: string;
  event_time: string;
  location: string;
  host_name: string;
  guest_name: string;
  guest_phone: string;
  status: string;
  requires_verification?: boolean;
  description?: string;
  companion_name?: string; // Legacy field (deprecated)
  companion_count?: number; // Legacy field (deprecated)
  allow_companions?: boolean;
  companion_limit?: number;
  guest_limit?: number;
  message_template?: string;
  editable_until?: string;
  link_expiry?: string;
  background_theme?: string;
  custom_gif_url?: string;
  custom_background_color?: string;
  image_url?: string;
  image_shape?: "circle" | "square" | "heart" | "star";
  location_link?: string;
  website_link?: string;
  dress_code?: string;
  contact_mirsal_link?: string;
  total_guests_invited?: number;
  // New Phase 3+ fields
  primary_guest?: CompanionInvitationData;
  companions?: CompanionInvitationData[];
  is_companion_invitation?: boolean;
  show_total_guests_invited?: boolean;
}


interface GuestDetailsFormData {
  first_name: string;
  last_name: string;
  email: string;
}

interface ResponseFormData {
  status: "accepted" | "rejected" | "pending";
  companion_name: string;
  companion_count: number;
}

const InvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { theme } = useAppSelector((state) => state.ui);
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventData, setEventData] = useState<EventData | null>(null);
  const [verified, setVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error" | "info">(
    "info"
  );

  // Track if data has been loaded to prevent re-loading
  const dataLoadedRef = useRef(false);
  const apiDataRef = useRef<any>(null);

  const [backgroundThemes, setBackgroundThemes] = useState<any>(defaultThemes);
  const [isDarkMode, setIsDarkMode] = useState(true); // Default to dark mode

  // Helper function to check if response deadline has passed
  const isResponseDeadlinePassed = () => {
    if (!eventData?.editable_until) return false;

    const now = new Date();
    const deadline = new Date(eventData.editable_until);
    const eventDateTime = new Date(
      `${eventData.event_date}T${eventData.event_time}`
    );

    // Use the earlier of deadline or event time
    const targetTime = deadline < eventDateTime ? deadline : eventDateTime;

    return now > targetTime;
  };

  // Show toast notification
  const showToastNotification = (
    message: string,
    type: "success" | "error" | "info" = "info"
  ) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  // Handle RSVP button click with toast confirmation
  const handleRSVPClick = async (
    status: "accepted" | "rejected" | "pending"
  ) => {
    if (isResponseDeadlinePassed()) {
      showToastNotification("Response deadline has passed", "error");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await apiClient.post(
        `/invitation/${token}/respond/`,
        {
          status,
          companion_name: responseForm.getValues("companion_name"),
          companion_count: responseForm.getValues("companion_count"),
        }
      );

      const result = response.data;

      if (result.success) {
        setEventData(result.event);
        responseForm.setValue("status", status);

        const statusMessages: Record<"accepted" | "rejected", string> = {
          accepted: "Marked as Attending",
          rejected: "Marked as Not Attending",
        };

        const message =
          status in statusMessages
            ? statusMessages[status as "accepted" | "rejected"]
            : "Response recorded";

        showToastNotification(message, "success");
        setShowRedirectModal(true); // Trigger the redirect modal
      } else {
        showToastNotification(
          result.message || "Failed to submit response",
          "error"
        );
      }
    } catch (err) {
      showToastNotification(
        "Failed to submit response. Please try again.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const guestDetailsForm = useForm<GuestDetailsFormData>({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
    },
  });

  const responseForm = useForm<ResponseFormData>({
    defaultValues: {
      status: "pending",
      companion_name: "",
      companion_count: 0,
    },
  });

  const loadInvitationData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!token) {
        setError("Invalid invitation link");
        setLoading(false);
        return;
      }

      // Use authenticated API call if user is logged in
      const response = await apiClient.get(`/invitation/${token}/`);
      const data = response.data;

      if (data.success) {
        // Always update event data with latest from API
        setEventData(data.event);
        apiDataRef.current = data.event; // Store in ref for persistence
        
        // Set guest details for editing
        if (data.event.guest_name) {
          const nameParts = data.event.guest_name.split(' ');
          guestDetailsForm.reset({
            first_name: nameParts[0] || "",
            last_name: nameParts.slice(1).join(' ') || "",
            email: "",
          });
        }

        // Set the current status in the response form
        if (data.event.status) {
          responseForm.reset({
            status: data.event.status as "accepted" | "rejected" | "pending",
            companion_name: data.event.companion_name || "",
            companion_count: data.event.companion_count || 0,
          });
        }
        
        setVerified(true);
      } else {
        logError(
          "API Error",
          new Error(data.message || "Invalid invitation link")
        );
        setError(data.message || "Invalid invitation link");
      }
    } catch (err) {
      logError("Network Error", err);
      setError("Failed to load invitation. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Load themes from API (only when authenticated)
  const loadThemes = useCallback(async () => {
    try {
      // Use apiClient which includes credentials
      const response = await apiClient.get(`/gifs/list/`);
      const result = response.data;
      
      if (result.success) {
        const themes: any = {};
        
        // Process themes array (includes both GIFs and colors)
        if (result.themes && Array.isArray(result.themes)) {
          result.themes.forEach((theme: any) => {
            if (theme.type === 'gif') {
              themes[theme.key] = {
                name: theme.name,
                description: theme.description,
                background: `url(${theme.url})`,
                preview: `url(${theme.url})`,
                fallback: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
                type: "gif",
                id: theme.id,
                filename: theme.filename,
                url: theme.url,
              };
            } else if (theme.type === 'color') {
              themes[theme.key] = {
                name: theme.name,
                description: theme.description,
                background: theme.color_code,
                preview: theme.color_code,
                fallback: theme.color_code,
                type: 'color',
                id: theme.id,
                color_code: theme.color_code,
              };
            }
          });
        }

        // Merge with default themes
        setBackgroundThemes({ ...defaultThemes, ...themes });
      }
    } catch (error) {
      // Silently fail - use default themes
      logError("Error loading themes", error);
      // Keep the default themes (don't override them)
    }
  }, []);

  // Check authentication and verify phone on component mount
  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link");
      setLoading(false);
      return;
    }

    const checkAuthAndLoad = async () => {
      if (isAuthenticated && user) {
        // User is logged in - verify phone matches
        try {
          const verifyResponse = await apiClient.get(
            `/invitation/${token}/verify-phone/`
          );
          
          if (verifyResponse.data.success && verifyResponse.data.phone_matches) {
            // Phone matches - load and show invitation
            await loadInvitationData();
            // Only load themes after successful authentication and verification
            await loadThemes();
          } else {
            // Phone doesn't match - redirect to dashboard
            const role = user.active_role || user.role || 'guest';
            navigate(`/${role}/dashboard`);
          }
        } catch (error: any) {
          // Verification failed - redirect to dashboard
          const role = user.active_role || user.role || 'guest';
          navigate(`/${role}/dashboard`);
        }
      } else {
        // User not logged in - redirect to login immediately
        // Don't call loadThemes() here as it requires authentication
        navigate(`/login?invitation_token=${token}&redirect=/invitation/${token}`);
      }
    };

    if (!dataLoadedRef.current) {
      dataLoadedRef.current = true;
      checkAuthAndLoad();
    }
  }, [token, isAuthenticated, user, navigate, loadInvitationData, loadThemes]);

  const handleRedirectToDashboard = async () => {
    setShowRedirectModal(false);
    // Switch to guest role first so multi-role users land on guest dashboard, not host/etc.
    const hasGuestRole = user?.roles?.includes("guest") || user?.role === "guest";
    const currentRole = user?.active_role || user?.role;
    if (hasGuestRole && currentRole !== "guest") {
      try {
        await roleSwitchingService.switchRole("guest");
      } catch (_) {
        // Still navigate even if switch fails (e.g. network)
      }
    }
    navigate("/guest/dashboard");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-900">
        <div className="relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm bg-black/80 border-gray-800/50">
          <div className="p-8 text-center">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg mx-auto mb-4 bg-gradient-to-br from-gray-700 to-gray-800 ring-1 ring-gray-600/20">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-[#33FF00]"></div>
            </div>
            <p className="text-lg font-medium text-white">
              Loading invitation...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !eventData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-900">
        <div className="relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm bg-black/80 border-gray-800/50">
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-red-900/30 text-red-300 border border-red-700/50">
              <XCircle className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-white">
              Invalid Invitation
            </h2>
            <p className="text-sm mb-6 text-gray-300">{error}</p>
            <button
              className="inline-flex items-center px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 bg-gradient-to-r from-[#33FF00] to-[#2BD400] text-black hover:from-[#2BD400] hover:to-[#33FF00] shadow-lg hover:shadow-[#33FF00]/25"
              onClick={() => navigate("/")}
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }


  // Guest details update removed - users can update from dashboard
  // Response submission success is now handled by the redirect modal

  return (
    <div
      className="min-h-screen w-screen relative overflow-x-hidden"
      style={(() => {
        const bgTheme =
          eventData?.background_theme || apiDataRef.current?.background_theme;
        const customGifUrl =
          eventData?.custom_gif_url || apiDataRef.current?.custom_gif_url;
        const customBackgroundColor =
          eventData?.custom_background_color || apiDataRef.current?.custom_background_color;
        
        // Priority 1: If there's a custom GIF URL, use it directly
        if (customGifUrl) {
          return {
            backgroundImage: `url(${customGifUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "fixed",
          };
        }
        
        // Priority 2: If there's a custom background color, use it directly
        if (customBackgroundColor) {
          return {
            backgroundColor: customBackgroundColor,
          };
        }
        
        // Priority 3: Check if it's a theme from backgroundThemes
        const selectedThemeData = bgTheme && backgroundThemes[bgTheme] ? backgroundThemes[bgTheme] : null;
        const isColorTheme = selectedThemeData?.type === 'color';
        
        if (isColorTheme) {
          // For static colors, use backgroundColor
          return {
            backgroundColor: selectedThemeData?.color_code || selectedThemeData?.background || '#f3f4f6',
          };
        }
        
        // For GIF themes, use backgroundImage
        if (bgTheme && backgroundThemes[bgTheme]) {
          return {
            backgroundImage: `${backgroundThemes[bgTheme].background}, ${backgroundThemes[bgTheme].fallback}`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "fixed",
          };
        }
        
        // Default fallback
        return {
          backgroundImage: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        };
      })()}
    >
      {/* Overlay for better text readability */}
      <div className="absolute inset-0" />

      <div className="relative z-10 min-h-screen flex items-start lg:items-center justify-center p-4 pt-16">
        <button
          onClick={handleRedirectToDashboard}
          className={`fixed top-4 left-4 z-50 inline-flex items-center px-4 py-2 rounded-full backdrop-blur-sm border shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-sm font-semibold ${
            isDarkMode
              ? "bg-gray-800/90 border-gray-700/50 text-white hover:bg-gray-700/90"
              : "bg-white/90 border-white/20 text-gray-900 hover:bg-white"
          }`}
        >
          <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
          Go to Dashboard
        </button>

        {/* Theme Toggle Button */}
        <button
          onClick={() => setIsDarkMode(!isDarkMode)}
          className="fixed top-4 right-4 z-50 w-12 h-12 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-white/20 dark:border-gray-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center"
        >
          {isDarkMode ? (
            <Sun className="h-6 w-6 text-yellow-500" />
          ) : (
            <Moon className="h-6 w-6 text-white" />
          )}
        </button>

        <div className="max-w-6xl w-full">
          <div className={`grid gap-8 items-start ${
            (eventData?.image_url || apiDataRef.current?.image_url)
              ? "grid-cols-1 lg:grid-cols-2"
              : "grid-cols-1 justify-items-center"
          }`}>
            {/* Left Side - Event Details in Card */}
            <div className={`space-y-6 ${
              (eventData?.image_url || apiDataRef.current?.image_url)
                ? "w-full order-2 lg:order-1"
                : "w-full max-w-2xl"
            }`}>
              {/* Event Information Card */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-900" : "bg-white/60"
                } backdrop-blur-xl rounded-2xl shadow-xl border ${
                  isDarkMode ? "border-gray-700/50" : "border-white/20"
                } p-8`
              }
                
              >
                {/* Event Title */}
                <div className="mb-6">
                  <h1
                    className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${
                      isDarkMode ? "text-white" : "text-black"
                    } mb-2`}
                  >
                    {eventData?.event_title || "Untitled Event"}
                  </h1>
                  <p
                    className={`text-base sm:text-lg flex flex-wrap items-center gap-2 ${
                      isDarkMode ? "text-white" : "text-black"
                    }`}
                  >
                    {eventData?.event_date ? (
                      <>
                        <span>{formatDate(eventData.event_date)}</span>
                        {eventData?.event_time && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span>{formatTime(eventData.event_time)}</span>
                          </>
                        )}
                      </>
                    ) : (
                      "Date & Time TBD"
                    )}
                  </p>
                </div>

                {/* Host Information */}
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-[#33FF00] to-[#2BD400] rounded-full flex items-center justify-center">
                    <span className="text-black font-semibold text-sm">
                      {eventData?.host_name?.charAt(0) || "A"}
                    </span>
                  </div>
                  <div>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-800"
                      }`}
                    >
                      Hosted by
                    </p>
                    <p
                      className={`font-semibold ${
                        isDarkMode ? "text-white" : "text-black"
                      }`}
                    >
                      {eventData?.host_name || "Amro"}
                    </p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start space-x-3 mb-4">
                  <MapPin
                    className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                      isDarkMode ? "text-gray-400" : "text-black"
                    }`}
                  />
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span
                      className={isDarkMode ? "text-gray-300" : "text-black"}
                    >
                      {eventData?.location || "No Location Set"}
                    </span>
                    {eventData?.location_link && (
                      <a
                        href={eventData.location_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center text-sm ${
                          isDarkMode
                            ? "text-[#33FF00] hover:text-[#2BD400]"
                            : "text-blue-600 hover:text-blue-700"
                        } underline`}
                      >
                        <LinkIcon className="h-3.5 w-3.5 mr-1" />
                        Open in Maps
                      </a>
                    )}
                  </div>
                </div>

                {/* Website Link */}
                {eventData?.website_link && (
                  <div className="flex items-center space-x-3 mb-4">
                    <Globe
                      className={`h-5 w-5 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    />
                    <a
                      href={eventData.website_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-sm ${
                        isDarkMode
                          ? "text-[#33FF00] hover:text-[#2BD400]"
                          : "text-blue-600 hover:text-blue-700"
                      } underline`}
                    >
                      Visit Event Website
                    </a>
                  </div>
                )}

                {/* Dress Code */}
                {eventData?.dress_code && (
                  <div className="flex items-center space-x-3 mb-4">
                    <Shirt
                      className={`h-5 w-5 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    />
                    <span
                      className={`text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Dress Code:{" "}
                      <span className="font-semibold">
                        {eventData.dress_code}
                      </span>
                    </span>
                  </div>
                )}

                {/* Guest Name */}
                {eventData?.guest_name && (
                  <div className="flex items-center space-x-3 mb-4">
                    <User
                      className={`h-5 w-5 ${
                        isDarkMode ? "text-gray-400" : "text-black"
                      }`}
                    />
                    <span
                      className={`text-sm ${
                        isDarkMode ? "text-gray-300" : "text-black"
                      }`}
                    >
                      Invited:{" "}
                      <span className="font-semibold">
                        {eventData.guest_name}
                      </span>
                    </span>
                  </div>
                )}


                {/* Contact Mirsal Link */}
                {eventData?.contact_mirsal_link && (
                  <div className="flex items-center space-x-3 mb-4">
                    <LinkIcon
                      className={`h-5 w-5 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    />
                    <a
                      href={eventData.contact_mirsal_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-sm ${
                        isDarkMode
                          ? "text-[#33FF00] hover:text-[#2BD400]"
                          : "text-blue-600 hover:text-blue-700"
                      } underline`}
                    >
                      Contact Mirsal
                    </a>
                  </div>
                )}
              </div>

              {/* RSVP Buttons Card */}
              {eventData && (
              <div
                className={`${
                  isDarkMode ? "bg-gray-900" : "bg-white/60"
                } backdrop-blur-xl rounded-2xl shadow-xl border ${
                  isDarkMode ? "border-gray-700/50" : "border-white/20"
                } p-4 sm:p-6 lg:p-8`}
              >
                  <h3
                    className={`text-lg font-semibold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    } mb-6 text-center`}
                  >
                    Will you attend this event?
                  </h3>

                  <div className="flex justify-center space-x-6">
                    {/* Attending Button */}
                    <button
                      onClick={() => handleRSVPClick("accepted")}
                      disabled={isResponseDeadlinePassed() || submitting}
                      className={`w-28 h-28 rounded-full flex flex-col items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg ${
                        isResponseDeadlinePassed() || submitting
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : responseForm.watch("status") === "accepted"
                          ? "bg-gradient-to-br from-[#33FF00] to-[#2BD400] text-black shadow-[#33FF00]/50"
                          : "bg-gradient-to-br from-gray-200 to-gray-300 text-gray-700 hover:from-[#33FF00]/20 hover:to-[#2BD400]/20 hover:text-white"
                      }`}
                    >
                      <CheckCircle className="h-8 w-8 mb-2" />
                      <span className="text-xs font-semibold text-center leading-tight px-1">Attending</span>
                    </button>

                    {/* Not Attending Button */}
                    <button
                      onClick={() => handleRSVPClick("rejected")}
                      disabled={isResponseDeadlinePassed() || submitting}
                      className={`w-28 h-28 rounded-full flex flex-col items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg ${
                        isResponseDeadlinePassed() || submitting
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : responseForm.watch("status") === "rejected"
                          ? "bg-gradient-to-br from-[#33FF00] to-[#2BD400] text-black shadow-[#33FF00]/50"
                          : "bg-gradient-to-br from-gray-200 to-gray-300 text-gray-700 hover:from-[#33FF00]/20 hover:to-[#2BD400]/20 hover:text-white"
                      }`}
                    >
                      <XCircle className="h-8 w-8 mb-2" />
                      <span className="text-xs font-semibold text-center leading-tight px-1">Not Attending</span>
                    </button>
                  </div>

                  {/* Companion Information Form */}
                  {eventData.allow_companions &&
                    responseForm.watch("status") === "accepted" && (
                      <div
                        className={`mt-8 p-6 bg-gradient-to-br from-[#33FF00]/10 to-[#2BD400]/10 border border-[#33FF00]/30 rounded-xl ${
                          isDarkMode
                            ? "bg-gradient-to-br from-[#33FF00]/20 to-[#2BD400]/20"
                            : ""
                        }`}
                      >
                        <h4
                          className={`text-lg font-semibold ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          } mb-4 flex items-center`}
                        >
                          <UserCheck className="h-5 w-5 mr-2 text-[#33FF00]" />
                          Companion Details
                        </h4>

                        <div className="space-y-4">
                          <div>
                            <label
                              className={`block text-sm font-medium ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              } mb-2`}
                            >
                              Companion Name (Optional)
                            </label>
                            <input
                              {...responseForm.register("companion_name")}
                              type="text"
                              placeholder="Enter companion's name"
                              className={`w-full px-4 py-3 border ${
                                isDarkMode
                                  ? "border-gray-600 bg-gray-800 text-white placeholder-gray-400"
                                  : "border-gray-300 text-gray-900 placeholder-gray-500"
                              } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#33FF00] focus:border-transparent`}
                            />
                          </div>

                          <div>
                            <label
                              className={`block text-sm font-medium ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              } mb-2`}
                            >
                              Number of Companions
                            </label>
                            <select
                              {...responseForm.register("companion_count", {
                                valueAsNumber: true,
                              })}
                              className={`w-full px-4 py-3 border ${
                                isDarkMode
                                  ? "border-gray-600 bg-gray-800 text-white"
                                  : "border-gray-300 text-gray-900"
                              } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#33FF00] focus:border-transparent`}
                            >
                              {Array.from(
                                {
                                  length: (eventData.companion_limit || 5) + 1,
                                },
                                (_, i) => (
                                  <option
                                    key={i}
                                    value={i}
                                    className={
                                      isDarkMode
                                        ? "bg-gray-800 text-white"
                                        : "bg-white text-gray-900"
                                    }
                                  >
                                    {i === 0
                                      ? "No companions"
                                      : `${i} companion${i > 1 ? "s" : ""}`}
                                  </option>
                                )
                              )}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              )}

              {/* Response Deadline Countdown Timer Card */}
              {eventData?.editable_until && (
                <div
                  className={`${
                    isDarkMode ? "bg-gray-900/60" : "bg-white/60"
                  } backdrop-blur-xl rounded-2xl shadow-xl border ${
                    isDarkMode ? "border-gray-700/50" : "border-white/20"
                  } p-6`}
                >
                  <h3
                    className={`text-base font-semibold ${
                      isDarkMode ? "text-red-400" : "text-red-800"
                    } mb-2 flex items-center`}
                  >
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></div>
                    Response Deadline
                  </h3>
                  <CountdownTimer
                    deadline={eventData.editable_until}
                    eventDate={eventData.event_date}
                    eventTime={eventData.event_time}
                    theme={isDarkMode ? "dark" : "light"}
                    className="text-sm"
                  />
                </div>
              )}
            </div>

            {/* Right Side - Event Image */}
            {(eventData?.image_url || apiDataRef.current?.image_url) && (
              <div className="flex flex-col items-center order-1 lg:order-2">
                {/* SVG Definition for Heart Clip Path - Needed for heart shape */}
                <svg width="0" height="0" style={{ position: "absolute" }}>
                  <defs>
                    <clipPath
                      id="heartClipPath"
                      clipPathUnits="objectBoundingBox"
                    >
                      {/* Classic heart shape: Start bottom center, two rounded arcs at top, meet at bottom point */}
                      <path
                        d="M0.5,1
                               C0.5,1 0,0.65 0,0.35
                               A0.25,0.25 0 0,1 0.5,0.35
                               A0.25,0.25 0 0,1 1,0.35
                               C1,0.65 0.5,1 0.5,1 Z"
                      />
                    </clipPath>
                  </defs>
                </svg>
                {/* Event Image */}
                <div className="w-full max-w-md">
                  {(() => {
                    const imageUrl =
                      eventData?.image_url || apiDataRef.current?.image_url;
                    const imageShape =
                      eventData?.image_shape || apiDataRef.current?.image_shape;
                    const shapeStyles = getImageShapeStyles(imageShape);
                    return (
                      <div
                        className={`relative w-full aspect-square overflow-hidden shadow-2xl ${
                          imageShape ? shapeStyles.className || "" : "rounded-2xl"
                        }`}
                        style={shapeStyles.style || {}}
                      >
                        <img
                          src={imageUrl}
                          alt="Event"
                          className={`w-full h-full object-contain ${
                            imageShape ? shapeStyles.className || "" : ""
                          }`}
                          style={imageShape ? shapeStyles.style || {} : {}}
                        />
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Redirect Notification Modal */}
      {showRedirectModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />
          
          {/* Modal */}
          <div className="relative w-full max-w-md">
            <div
              className={`relative w-full transform overflow-hidden rounded-lg shadow-xl transition-all ${
                theme === 'dark' 
                  ? 'bg-gray-900/95 border border-gray-700/50' 
                  : 'bg-white border border-gray-200/50'
              }`}
            >
              {/* Content */}
              <div className="px-6 py-5">
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 flex items-center justify-center rounded-full p-2 ${
                    theme === 'dark' ? 'bg-[#33FF00]/20' : 'bg-[#33FF00]/10'
                  }`}>
                    <CheckCircle className={`h-6 w-6 ${
                      theme === 'dark' ? 'text-[#33FF00]' : 'text-[#2BD400]'
                    }`} />
                  </div>

                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Response Submitted Successfully!
                    </h3>
                    <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      Thank you for responding to the invitation. You will now be redirected to your guest dashboard where you can view all your invitations and manage your RSVPs.
                    </p>
                  </div>
                </div>

                {/* Action Button */}
                <div className="mt-6 flex items-center justify-end">
                  <button
                    onClick={handleRedirectToDashboard}
                    className={`inline-flex items-center px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 bg-gradient-to-r from-[#33FF00] to-[#2BD400] text-black hover:from-[#2BD400] hover:to-[#33FF00] shadow-lg hover:shadow-[#33FF00]/25`}
                  >
                    Go to Dashboard
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
          <div
            className={`px-6 py-4 rounded-lg shadow-lg backdrop-blur-sm border transition-all duration-300 ${
              toastType === "success"
                ? "bg-green-500/90 text-white border-green-400/50"
                : toastType === "error"
                ? "bg-red-500/90 text-white border-red-400/50"
                : "bg-blue-500/90 text-white border-blue-400/50"
            }`}
          >
            <div className="flex items-center space-x-3">
              {toastType === "success" && <CheckCircle className="h-5 w-5" />}
              {toastType === "error" && <XCircle className="h-5 w-5" />}
              {toastType === "info" && <AlertCircle className="h-5 w-5" />}
              <span className="font-medium">{toastMessage}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationPage;
